import React from 'react';
import { X, Trophy } from 'lucide-react';
import { usePickemLeaderboard } from '@/api/query/useLeaderboard';
import { Button } from '@/components/ui/button';
import EmptyState from '@/components/shared/EmptyState';

interface PickemLeaderboardProps {
  pickemId: string;
  leagueId?: string;
  isOpen: boolean;
  onClose: () => void;
  currentUserId?: string;
}

export const PickemLeaderboard: React.FC<PickemLeaderboardProps> = ({
  pickemId,
  leagueId,
  isOpen,
  onClose,
  currentUserId,
}) => {
  const { data, isLoading, error } = usePickemLeaderboard({
    pickemId,
    leagueId,
  });

  if (!isOpen) return null;

  // Flatten all pages into single leaderboard array
  const leaderboard = data?.pages.flatMap(page => page.leaderboard) || [];
  const MAX_ROWS = 10;

  // --- ID helpers -------------------------------------------------
  const normaliseId = (id: unknown) => (id == null ? null : String(id));
  const currentUserIdNorm = normaliseId(currentUserId);

  const getEntryUserId = (entry: any) => normaliseId(entry.user_id ?? entry.userId ?? entry.id);

  // --- Build displayedLeaderboard ---------------------------------
  const baseTop = leaderboard.slice(0, MAX_ROWS);
  let displayedLeaderboard: any[] = baseTop;

  if (currentUserIdNorm) {
    const currentUserEntry = leaderboard.find(entry => getEntryUserId(entry) === currentUserIdNorm);

    if (currentUserEntry) {
      // Pinned row at the top, marked as a header
      const currentUserHeader = {
        ...currentUserEntry,
        __currentUserHeader: true,
      };

      // We keep baseTop as-is so if the user is in the top 10,
      // they will also appear again as a normal ranked row.
      displayedLeaderboard = [currentUserHeader, ...baseTop];
    }
    // else: user not in leaderboard data – just baseTop
  }

  return (
    <div
      data-test-id="pickem-leaderboard-overlay"
      className="fixed inset-0 z-50 bg-app-background/95 backdrop-blur-sm"
    >
      <div className="h-full flex flex-col">
        <div className="bg-app-surface-elevated border-b border-border/20 px-4 py-4 flex items-center justify-between">
          <h2 className="text-text-primary font-bold text-lg">Pickem Leaderboard</h2>
          <Button
            data-test-id="close-leaderboard-btn"
            variant="ghost"
            size="sm"
            onClick={onClose}
            className="text-text-secondary hover:text-text-primary"
          >
            <X className="w-5 h-5" />
          </Button>
        </div>

        <div className="flex-1 overflow-y-auto">
          {isLoading && (
            <div className="p-8 text-center">
              <div className="text-text-secondary">Loading leaderboard...</div>
            </div>
          )}

          {error && (
            <div className="p-8">
              <EmptyState title="Failed to load leaderboard" showAdSpace={false} />
            </div>
          )}

          {!isLoading && !error && leaderboard.length === 0 && (
            <div className="p-8">
              <EmptyState title="No leaderboard data available" showAdSpace={false} />
            </div>
          )}

          {!isLoading && !error && leaderboard.length > 0 && (
            <div className="divide-y divide-border/10">
              {displayedLeaderboard.map((entry: any) => {
                const entryUserId = getEntryUserId(entry);
                const isCurrentUser = !!currentUserIdNorm && entryUserId === currentUserIdNorm;
                const isHeader = entry.__currentUserHeader === true;
                const isHighlighted = isHeader;

                return (
                  <div
                    key={
                      isHeader
                        ? `current-header-${entryUserId}`
                        : `rank-${entry.rank}-${entryUserId}`
                    }
                    data-test-id={
                      isHeader ? 'leaderboard-row-current-user' : `leaderboard-row-${entry.rank}`
                    }
                    data-test-state={
                      isHeader
                        ? 'current-user-header'
                        : isCurrentUser
                          ? 'current-user'
                          : 'other-user'
                    }
                    className={`flex items-center justify-between p-4 transition-smooth ${
                      isHighlighted ? 'bg-bet99-primary text-white' : 'hover:bg-game-card-active'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      {/* Rank badge: hide rank for the pinned header row only */}
                      {!isHeader && (
                        <div
                          className={`w-10 h-10 rounded-lg flex items-center justify-center font-bold text-sm ${
                            isHeader
                              ? 'bg-white text-bet99-primary'
                              : 'bg-app-surface-elevated text-text-primary'
                          }`}
                        >
                          {entry.rank}
                        </div>
                      )}

                      <div>
                        <span
                          className={`font-bold ${
                            isHighlighted ? 'text-white' : 'text-text-primary'
                          }`}
                        >
                          <span className="inline-flex items-center gap-2">
                            <span>
                              {entry.username}
                              {isCurrentUser && ' (You)'}
                            </span>

                            {entry.rank === 1 && (
                              <span className="inline-flex items-center gap-1">
                                <Trophy
                                  data-test-id="trophy-icon"
                                  className="w-4 h-4 text-yellow-500"
                                />
                                <span
                                  data-test-id="prize-label"
                                  className={`
                                    text-xs font-semibold px-2 py-0.5 rounded-full
                                    ${
                                      isHighlighted
                                        ? 'bg-white/20 text-white'
                                        : 'bg-app-surface-elevated text-text-secondary'
                                    }
                                  `}
                                ></span>
                              </span>
                            )}
                          </span>
                        </span>
                      </div>
                    </div>

                    <div
                      className={`font-bold ${isHighlighted ? 'text-white' : 'text-text-primary'}`}
                    >
                      {entry.points}pts
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="bg-app-surface-elevated border-t border-border/20 p-4">
          <Button
            data-test-id="close-pickem-leaderboard-btn"
            variant="bet99"
            onClick={onClose}
            className="w-full"
          >
            Close
          </Button>
        </div>
      </div>
    </div>
  );
};

export default PickemLeaderboard;
